import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import {
  AcceptGoldenCandidateBodySchema,
  AcceptGoldenCandidateResponseSchema,
  AddGoldenUserTurnBodySchema,
  AddGoldenUserTurnResponseSchema,
  CreateGoldenConversationBodySchema,
  CreateGoldenConversationResponseSchema,
  DeleteGoldenConversationResponseSchema,
  GenerateGoldenCandidateResponseSchema,
  GetGoldenConversationResponseSchema,
  ListGoldenConversationsParamsSchema,
  ListGoldenConversationsResponseSchema,
  ReadyGoldenConversationResponseSchema,
  SetGoldenCheckpointBodySchema,
  SetGoldenCheckpointResponseSchema,
  SetGoldenTurnContentBodySchema,
  SetGoldenTurnContentResponseSchema
} from "../../public/v1/schemas/golden-conversation.schemas";

/**
 * ZGoldenConversations — internal contract for golden-conversation authoring
 * (Prompt Lab phase 6, the browser twin of `nexus eval conv`). Same shapes as
 * the public v1 surface: the schemas are imported from
 * `public/v1/schemas/golden-conversation.schemas`, the direction phase 1's
 * prompt-variants contract already established, wrapped in the internal
 * success envelope.
 *
 * All routes are nested under /api/prompt-eval/golden-conversations and every
 * one is gated on the PROMPT_EVAL feature flag by the controller.
 *
 * `index` is a 0-based turn position typed here as a STRING, because a path
 * variable is one: the generated client substitutes `PathVars` into the URL
 * through a `Record<string, string>`, and the backend's param schema coerces
 * the segment back to a number. Both turn-scoped routes carry `conversationId`
 * AND `index` in `PathVars` — `path-vars-validator.ts` blocks generation for
 * the whole app on any mismatch.
 */
export const ZGoldenConversations = {
  /** GET /api/prompt-eval/golden-conversations — the org's conversations, newest first */
  List: {
    method: "GET" as const,
    path: "/api/prompt-eval/golden-conversations",
    Query: ListGoldenConversationsParamsSchema,
    Response: ApiSuccessSchema(ListGoldenConversationsResponseSchema)
  },

  /** POST /api/prompt-eval/golden-conversations — create (DRAFT, no turns) */
  Create: {
    method: "POST" as const,
    path: "/api/prompt-eval/golden-conversations",
    Body: CreateGoldenConversationBodySchema,
    Response: ApiSuccessSchema(CreateGoldenConversationResponseSchema)
  },

  /** GET /api/prompt-eval/golden-conversations/:conversationId — with its full turn list */
  Get: {
    method: "GET" as const,
    path: "/api/prompt-eval/golden-conversations/:conversationId",
    PathVars: z.object({ conversationId: z.string() }),
    Response: ApiSuccessSchema(GetGoldenConversationResponseSchema)
  },

  /** DELETE /api/prompt-eval/golden-conversations/:conversationId — turns cascade */
  Delete: {
    method: "DELETE" as const,
    path: "/api/prompt-eval/golden-conversations/:conversationId",
    PathVars: z.object({ conversationId: z.string() }),
    Response: ApiSuccessSchema(DeleteGoldenConversationResponseSchema)
  },

  /** POST /api/prompt-eval/golden-conversations/:conversationId/turns/user — append a user turn */
  AddUserTurn: {
    method: "POST" as const,
    path: "/api/prompt-eval/golden-conversations/:conversationId/turns/user",
    PathVars: z.object({ conversationId: z.string() }),
    Body: AddGoldenUserTurnBodySchema,
    Response: ApiSuccessSchema(AddGoldenUserTurnResponseSchema)
  },

  /**
   * POST /api/prompt-eval/golden-conversations/:conversationId/generate
   *
   * A live agent turn in a fresh ephemeral session: seconds of latency, real
   * tool execution, real spend. The reply lands as the conversation's single
   * pending candidate and replaces any previous one.
   */
  Generate: {
    method: "POST" as const,
    path: "/api/prompt-eval/golden-conversations/:conversationId/generate",
    PathVars: z.object({ conversationId: z.string() }),
    Response: ApiSuccessSchema(GenerateGoldenCandidateResponseSchema)
  },

  /** POST /api/prompt-eval/golden-conversations/:conversationId/accept — candidate → golden turn */
  Accept: {
    method: "POST" as const,
    path: "/api/prompt-eval/golden-conversations/:conversationId/accept",
    PathVars: z.object({ conversationId: z.string() }),
    Body: AcceptGoldenCandidateBodySchema,
    Response: ApiSuccessSchema(AcceptGoldenCandidateResponseSchema)
  },

  /** PUT /api/prompt-eval/golden-conversations/:conversationId/turns/:index/content */
  SetTurnContent: {
    method: "PUT" as const,
    path: "/api/prompt-eval/golden-conversations/:conversationId/turns/:index/content",
    PathVars: z.object({ conversationId: z.string(), index: z.string() }),
    Body: SetGoldenTurnContentBodySchema,
    Response: ApiSuccessSchema(SetGoldenTurnContentResponseSchema)
  },

  /** PUT /api/prompt-eval/golden-conversations/:conversationId/turns/:index/checkpoint */
  SetCheckpoint: {
    method: "PUT" as const,
    path: "/api/prompt-eval/golden-conversations/:conversationId/turns/:index/checkpoint",
    PathVars: z.object({ conversationId: z.string(), index: z.string() }),
    Body: SetGoldenCheckpointBodySchema,
    Response: ApiSuccessSchema(SetGoldenCheckpointResponseSchema)
  },

  /** POST /api/prompt-eval/golden-conversations/:conversationId/ready — close authoring */
  Ready: {
    method: "POST" as const,
    path: "/api/prompt-eval/golden-conversations/:conversationId/ready",
    PathVars: z.object({ conversationId: z.string() }),
    Response: ApiSuccessSchema(ReadyGoldenConversationResponseSchema)
  }
} as const;
